import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, GraduationCap, Users, School, Bookmark, Sparkles, Compass, CalendarCheck,
  UserPlus, FileText, ArrowRight, Building2, Clock, CheckCircle2, Inbox,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth, roleLabel } from "../context/AuthContext";

const roleIcon = { student: GraduationCap, counsellor: Users, school: School };

const QUICK = {
  student: [
    { icon: Compass, title: "Course Finder", desc: "Find your best-fit course in 60 seconds", to: "/course-finder" },
    { icon: Sparkles, title: "Career Quiz", desc: "Discover streams that match your interests", to: "/assessment" },
    { icon: Bookmark, title: "My Saved Items", desc: "Shortlist, matches and alerts", to: "/saved" },
    { icon: CalendarCheck, title: "Book a Session", desc: "Talk to a career counsellor", to: "/", scroll: "contact" },
  ],
  counsellor: [
    { icon: UserPlus, title: "Complete Your Profile", desc: "Register to join our counsellor network", to: "/", scroll: "join" },
    { icon: Users, title: "Counsellor Directory", desc: "See how your public profile appears", to: "/counsellors" },
    { icon: FileText, title: "Career Resources", desc: "Read the latest guidance articles", to: "/blog" },
  ],
  school: [
    { icon: Building2, title: "Partner With Us", desc: "Bring career counselling to your campus", to: "/", scroll: "contact" },
    { icon: Compass, title: "Course Finder", desc: "A tool your students will love", to: "/course-finder" },
    { icon: FileText, title: "For Schools Guide", desc: "How we build a counselling culture", to: "/", scroll: "schools" },
  ],
};

export default function Account() {
  const navigate = useNavigate();
  const { user, loading, logout, get } = useAuth();
  const [bookings, setBookings] = useState(null);
  const [counsellor, setCounsellor] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    if (user.role === "student") {
      get("/my/appointments").then((r) => setBookings(r.data)).catch(() => setBookings([]));
    }
    if (user.role === "counsellor") {
      get("/my/counsellor").then((r) => setCounsellor(r.data)).catch(() => setCounsellor({ registered: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#5a6274]">Loading...</div>
    );
  }

  const Icon = roleIcon[user.role] || GraduationCap;
  const cards = QUICK[user.role] || [];
  const go = (c) => { if (c.scroll) navigate(c.to, { state: { scrollTo: c.scroll } }); else navigate(c.to); };

  return (
    <div className="min-h-screen bg-[#f6f8fc] flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="bg-[#16244F] py-12 md:py-14">
          <div className="max-w-[1100px] mx-auto px-5 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/10 text-[#F9B57C] flex items-center justify-center"><Icon size={28} /></div>
              <div>
                <h1 className="font-head text-2xl md:text-3xl font-bold text-white">Hi, {user.name.split(" ")[0]}!</h1>
                <span className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-0.5 rounded-full bg-[#F58220]/20 text-[#F9B57C] text-xs font-semibold">{roleLabel(user.role)} Account</span>
              </div>
            </div>
            <button onClick={() => { logout(); navigate("/"); }} className="inline-flex items-center gap-1.5 text-white/80 hover:text-white font-head font-semibold text-sm"><LogOut size={16} /> Logout</button>
          </div>
        </section>

        <section className="max-w-[1100px] mx-auto px-5 py-10 md:py-12">
          <h2 className="font-head text-xl font-bold text-[#16244F] mb-6">Quick actions for you</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((c) => (
              <button key={c.title} onClick={() => go(c)} className="text-left bg-white rounded-2xl cc-shadow-card p-6 group hover:-translate-y-1 transition-transform duration-300">
                <div className="w-12 h-12 rounded-xl bg-[#eaf3ff] text-[#2b8cf0] flex items-center justify-center mb-4 group-hover:bg-[#2b8cf0] group-hover:text-white transition-colors"><c.icon size={24} /></div>
                <h3 className="font-head font-semibold text-[#16244F]">{c.title}</h3>
                <p className="text-sm text-[#5a6274] mt-1">{c.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-[#F58220] font-head font-semibold text-sm group-hover:gap-2 transition-all">Open <ArrowRight size={15} /></span>
              </button>
            ))}
          </div>

          {/* Student: appointment history */}
          {user.role === "student" && (
            <div className="mt-10">
              <h2 className="font-head text-xl font-bold text-[#16244F] mb-5 flex items-center gap-2"><Inbox size={20} className="text-[#2b8cf0]" /> My Appointments</h2>
              {bookings == null ? (
                <div className="bg-white rounded-2xl cc-shadow-card p-6 text-[#8a90a0]">Loading...</div>
              ) : bookings.length === 0 ? (
                <div className="bg-white rounded-2xl cc-shadow-card p-8 text-center">
                  <p className="text-[#5a6274]">You haven't booked any sessions yet.</p>
                  <button onClick={() => navigate("/", { state: { scrollTo: "contact" } })} className="mt-4 inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold px-5 py-2.5 rounded-lg transition-colors">Book a Session</button>
                  <p className="text-xs text-[#aab0bf] mt-3">Tip: use this account email ({user.email}) when booking so it shows up here.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl cc-shadow-card divide-y divide-gray-100">
                  {bookings.map((b) => (
                    <div key={b.id} className="p-5 flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-head font-semibold text-[#16244F]">Career Counselling Session</span>
                          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${b.status === "scheduled" ? "bg-[#16a34a]/15 text-[#16a34a]" : b.status === "new" ? "bg-[#F58220]/15 text-[#F58220]" : "bg-[#2b8cf0]/15 text-[#2b8cf0]"}`}>{b.status}</span>
                        </div>
                        {b.message && <p className="text-sm text-[#5a6274] mt-1.5">{b.message}</p>}
                        {b.assigned_counsellor_name && <p className="text-xs text-[#16a34a] mt-1.5 font-semibold">Counsellor: {b.assigned_counsellor_name}</p>}
                      </div>
                      <span className="text-xs text-[#8a90a0] whitespace-nowrap flex items-center gap-1"><Clock size={13} /> {new Date(b.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Counsellor: application status */}
          {user.role === "counsellor" && (
            <div className="mt-10">
              <h2 className="font-head text-xl font-bold text-[#16244F] mb-5 flex items-center gap-2"><UserPlus size={20} className="text-[#2b8cf0]" /> My Application</h2>
              {counsellor == null ? (
                <div className="bg-white rounded-2xl cc-shadow-card p-6 text-[#8a90a0]">Loading...</div>
              ) : !counsellor.registered ? (
                <div className="bg-white rounded-2xl cc-shadow-card p-8 text-center">
                  <p className="text-[#5a6274]">You haven't submitted your counsellor profile yet.</p>
                  <button onClick={() => navigate("/", { state: { scrollTo: "join" } })} className="mt-4 inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold px-5 py-2.5 rounded-lg transition-colors">Register as Counsellor</button>
                  <p className="text-xs text-[#aab0bf] mt-3">Use this account email ({user.email}) so we can link your application.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl cc-shadow-card p-6 flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${counsellor.status === "approved" ? "bg-[#16a34a]/10 text-[#16a34a]" : counsellor.status === "rejected" ? "bg-red-100 text-red-500" : "bg-[#F58220]/10 text-[#F58220]"}`}>
                    {counsellor.status === "approved" ? <CheckCircle2 size={24} /> : <Clock size={24} />}
                  </div>
                  <div>
                    <div className="font-head font-semibold text-[#16244F] capitalize">Application {counsellor.status}</div>
                    <p className="text-sm text-[#5a6274]">
                      {counsellor.status === "approved" ? "You're live in our counsellor directory!" : counsellor.status === "rejected" ? "Please contact us for details." : "Our team is reviewing your profile."}
                      {counsellor.specialization ? ` · ${counsellor.specialization}` : ""}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-10 rounded-2xl bg-[#eaf3ff] p-7 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-head text-lg font-bold text-[#16244F]">Need personalised guidance?</h3>
              <p className="text-[#5a6274] text-sm mt-1">Our expert counsellors are here to help you take the next step.</p>
            </div>
            <button onClick={() => navigate("/", { state: { scrollTo: "contact" } })} className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold px-6 py-3 rounded-lg transition-colors whitespace-nowrap">Book an Appointment</button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
